import React, { useEffect, useState } from "react";
import {
  Typography,
  Form,
  Input,
  Button,
  Divider,
  Card,
  notification,
  message,
} from "antd";
import Logo from "assets/new-hire-logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RoleTypes } from "enum/role-type-enum";
import { useLoginEnumerator } from "api/enumerator/Login/useEnumeratorLogin";
import { useLoginRecruiter } from "api/recruiter/Login/useRecruiterLogin";
import GoogleIconSvg from "assets/devicon_google.svg";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth, db } from "../../../firebase/firebase.js";
import { query, where, collection, getDocs } from "firebase/firestore";
import { storage } from "utils/storage";
import { userCreateInterface } from "api/register/dto";

const { Title, Text } = Typography;
const getSafeRedirect = (raw?: string | null): string | null => {
  if (!raw) return null;
  try {
    const dest = new URL(raw, window.location.origin); // handles relative paths
    return dest.origin === window.location.origin
      ? dest.pathname + dest.search + dest.hash
      : null;
  } catch {
    return null;
  }
};

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate     = useNavigate();
  const location     = useLocation();
  const queryParams  = new URLSearchParams(location.search);

  const queryValue   = queryParams.get("roleType")   || "";      
  const redirectRaw  = queryParams.get("redirectUrl");
  const redirectUrl  = getSafeRedirect(redirectRaw);             

  const loginEnumeratorMutation = useLoginEnumerator();
  const loginRecruiterMutation  = useLoginRecruiter();

  const [isLoading, setIsLoading] = useState(false);

  /* ───────────────────────────────────────────────────
     Auto-redirect if the user is already logged-in
     ─────────────────────────────────────────────────── */
  useEffect(() => {
    const storedUser = storage.getUserFromStorage();
    if (!storedUser || location.pathname !== "/login") return;

    if (redirectUrl) {
      navigate(redirectUrl);
      return;
    }

    if (storedUser.role?.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()) {
      navigate("/recruiter/home");
    } else if (
      storedUser.role?.toLowerCase() === RoleTypes.ENUMERATOR.toLowerCase()
    ) {
      navigate("/enumerator/home");
    }
  }, [navigate, location, redirectUrl]);

  /* ───────────────────────────────────────────────────
     Email / Password login
     ─────────────────────────────────────────────────── */
  const handleSubmit = async () => {
    try {
      const parameters = await form.validateFields();
      if (!queryValue) return;

      const payload = {
        emailOrPhone: parameters.email,
        role: queryValue,
        password: parameters.password,
      };

      setIsLoading(true);

      const afterSuccess = () => {
        if (redirectUrl) navigate(redirectUrl);
        else if (
          queryValue.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()
        ) {
          navigate("/recruiter/home");
        } else {
          navigate("/enumerator/home");
        }
      };

      if (queryValue.toLowerCase() === RoleTypes.ENUMERATOR.toLowerCase()) {
        loginEnumeratorMutation.mutate(payload, {
          onSuccess: afterSuccess,
          onSettled: () => setIsLoading(false),
        });
      } else if (queryValue.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()) {
        loginRecruiterMutation.mutate(payload, {
          onSuccess: afterSuccess,
          onSettled: () => setIsLoading(false),
        });
      }
    } catch {
      setIsLoading(false);
    }
  };

  /* ───────────────────────────────────────────────────
     Google auth
     ─────────────────────────────────────────────────── */
  const handleGoogleAuth = async () => {
    if (!queryValue) {
      notification.error({
        message: "Missing Role",
        description: "Role type is missing from the URL.",
      });
      return;
    }

    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user   = result.user;

      if (user.providerData[0].providerId !== "google.com") {
        message.error(
          "Your email is registered, but not with a Google account. Please try another method."
        );
        return;
      }

      if (!user.email) throw new Error("User email is missing from Google.");

      // Verify role stored in Firestore
      const qSnap = await getDocs(
        query(collection(db, "user_infos"), where("uid", "==", user.uid))
      );

      if (qSnap.empty) throw new Error("User not registered.");

      const existing = qSnap.docs[0].data() as userCreateInterface;

      if (
        existing.role?.toLowerCase() !== queryValue.toLowerCase()
      ) {
        notification.error({
          message: "Login Error",
          description: `Your registered role (${existing.role}) does not match the chosen login method.`,
        });
        return;
      }

      const idToken = await user.getIdToken();

      const userInfo: userCreateInterface = {
        email: user.email,
        role:  existing.role,
        uid:   user.uid,
        firstName: user.displayName?.split(" ")[0] || "",
        lastName:  user.displayName?.split(" ")[1] || "",
        createdAt: existing.createdAt,
        isVerified: existing.isVerified,
      };

      storage.setUserInStorage(userInfo);
      storage.setTokenInStorage(idToken);

      // final navigation
      if (redirectUrl) navigate(redirectUrl);
      else if (
        queryValue.toLowerCase() === RoleTypes.RECRUITER.toLowerCase()
      ) {
        navigate("/recruiter/home");
      } else {
        navigate("/enumerator/home");
      }

      message.success("You have successfully logged in.");
    } catch (error) {
      notification.error({
        message: "Google Sign-In Failed",
        description:
          error instanceof Error ? error.message : "An error occurred.",
      });
    }
  };

  return (
    <div className="h-screen bg-background overflow-auto px-5 md:px-0">
      <div className="max-w-lg mx-auto my-6">
        <div className="flex justify-center mb-0">
          <img alt="logo" src={Logo} width={350} />
        </div>

        <Card className="px-4 py-6">
          <Title level={2}>Account sign in</Title>
          <Text type="secondary">
            Welcome to the login page. You can either log in, or register a new
            account if you don't already have one.
          </Text>

          <div className="mt-12">
            <Form layout="vertical" form={form} onFinish={handleSubmit}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, message: "Email is required" }]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>

              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "Password is required" }]}
              >
                <Input.Password placeholder="Enter password here" />
              </Form.Item>

              <Button
                loading={isLoading}
                htmlType="submit"
                type="primary"
                className="h-12 px-8 border-2 border-border text-base flex justify-center items-center gap-2 bg-primary text-white border-none w-full mt-12"
              >
                Login
              </Button>
            </Form>

            <Divider>Or</Divider>

            <div className="flex flex-col gap-4">
              <Button
                className="h-12 px-8 border-1 border-grey text-base font-bold flex justify-center items-center gap-2 w-full"
                onClick={handleGoogleAuth}
              >
                <img alt="Google Icon" src={GoogleIconSvg} />
                Continue with Google
              </Button>

            </div>

            {/* ───── Forgot password link ───── */}
            <div className="mt-8">
              <Text className="text-base">
                Don't remember your password?{" "}
                <Link
                  to={
                    `/forgot-password?roleType=${queryValue}` +
                    (redirectRaw ? `&redirectUrl=${encodeURIComponent(redirectRaw)}` : "")
                  }
                >
                  Click here.
                </Link>
              </Text>
            </div>

            {/* ───── Back button ───── */}
            <div className="mt-8">
              <Button
                type="default"
                onClick={() => navigate(-1)}
                className="h-12 px-8 border-2 border-border text-base flex justify-center items-center gap-2 w-full"
              >
                Back
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
